/**
 * Form Buku Tamu Publik
 * Halaman publik untuk mengisi buku tamu
 * Dapat diakses tanpa login dengan link: DOMAIN_ESA_OFFICE/publik/buku-tamu/{link}
 */

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";

// @mui material components
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 3 PRO React themes
import theme from "assets/theme";

// Images
import esaOfficeBanner from "assets/images/eo_cover.png";
import eo_mini1 from "assets/images/eo_mini1.png";

// Utils
import { API_ESA_OFFICE } from "utils";
import Icon from "@mui/material/Icon";

// List keperluan umum untuk kantor pemerintah
const listKeperluan = [
  "Konsultasi",
  "Pengurusan Surat Keterangan",
  "Pengurusan Izin",
  "Pengurusan Dokumen",
  "Koordinasi",
  "Rapat",
  "Survey",
  "Kunjungan Kerja",
  "Pengaduan",
  "Permohonan Informasi",
  "Verifikasi Data",
  "Koordinasi Program",
  "Pembahasan Proyek",
  "Audit",
  "Inspeksi",
  "Sosialisasi",
  "Pelatihan",
  "Bimbingan Teknis",
  "Permintaan Data dan Informasi",
];

function FormBukuTamuPublik() {
  // Link dari URL parameter
  const { link } = useParams();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bukuDinas, setBukuDinas] = useState(null);
  const [bidangList, setBidangList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [allUsersList, setAllUsersList] = useState([]); // Store all users untuk filtering
  const [formData, setFormData] = useState({
    nama: "",
    nama_instansi: "",
    keperluan: "",
    bidang_id: null,
    bertemu_dengan: null,
    id_smart: null,
  });

  // State untuk dialog rating
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0); // 0 = belum dipilih, 1-5 = rating

  // State untuk deteksi mobile
  const [isMobile, setIsMobile] = useState(false);

  // Deteksi ukuran layar untuk mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 960); // md breakpoint biasanya 960px
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Pilih banner berdasarkan ukuran layar
  const bannerImage = isMobile ? eo_mini1 : esaOfficeBanner;

  useEffect(() => {
    if (link) {
      fetchBukuDinas(link);
    }
  }, [link]);

  const fetchBukuDinas = async (linkParam) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}buku-tamu/buku-dinas/link/${linkParam}`
      );
      setBukuDinas(response.data);

      // Fetch bidang dan users jika ada id_dinas
      if (response.data.id_dinas) {
        fetchBidang(response.data.id_dinas);
        fetchUsers(response.data.id_dinas);
      }

      // Set data dari user yang login jika ada
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (user) {
          const updateData = {
            id_smart: user.uid_RSS || null,
          };

          // Set nama dari user jika ada
          if (user.nama) {
            updateData.nama = user.nama;
          }

          // Set nama_instansi dari nama_dinas user jika ada
          // User object mungkin memiliki nama_dinas langsung atau perlu fetch
          if (user.nama_dinas) {
            updateData.nama_instansi = user.nama_dinas;
          } else if (user.dinas_id) {
            // Jika tidak ada nama_dinas, fetch dari API
            try {
              const dinasResponse = await axios.get(
                `${API_ESA_OFFICE}dinas/${user.dinas_id}`
              );
              if (dinasResponse.data && dinasResponse.data.nama_dinas) {
                updateData.nama_instansi = dinasResponse.data.nama_dinas;
              }
            } catch (dinasError) {
              console.error("Error fetching dinas:", dinasError);
              // Continue tanpa nama_instansi
            }
          }

          setFormData((prev) => ({
            ...prev,
            ...updateData,
          }));
        }
      } catch (e) {
        // Ignore jika tidak ada user di localStorage
      }
    } catch (error) {
      console.error("Error fetching buku dinas:", error);
      swal({
        title: "Error",
        text: "Link buku tamu tidak valid atau tidak ditemukan",
        icon: "error",
        button: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBidang = async (dinasId) => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}buku-tamu/bidang/${dinasId}`
      );
      setBidangList(response.data || []);
    } catch (error) {
      console.error("Error fetching bidang:", error);
    }
  };

  const fetchUsers = async (dinasId, bidangId = null) => {
    try {
      let url = `${API_ESA_OFFICE}buku-tamu/users/${dinasId}`;
      if (bidangId) {
        url += `?bidang_id=${bidangId}`;
      }
      const response = await axios.get(url);
      const users = response.data || [];
      setUsersList(users);
      // Simpan semua users untuk referensi
      if (!bidangId) {
        setAllUsersList(users);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBidangChange = (event, newValue) => {
    const bidangId = newValue ? newValue.bidang_id : null;
    setFormData((prev) => ({
      ...prev,
      bidang_id: bidangId,
      bertemu_dengan: null, // Reset bertemu_dengan ketika bidang berubah
    }));

    // Filter users berdasarkan bidang yang dipilih
    if (bukuDinas && bukuDinas.id_dinas) {
      if (bidangId) {
        fetchUsers(bukuDinas.id_dinas, bidangId);
      } else {
        // Jika bidang tidak dipilih, tampilkan semua users
        fetchUsers(bukuDinas.id_dinas);
      }
    }
  };

  const handleBertemuDenganChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      bertemu_dengan: newValue ? newValue.uid_RSS : null,
    }));
  };

  const handleKeperluanChange = (event, newValue) => {
    // newValue bisa berupa string (jika freeSolo) atau null
    setFormData((prev) => ({
      ...prev,
      keperluan: newValue || "",
    }));
  };

  const handleSubmit = () => {
    if (!formData.nama_instansi || !formData.keperluan) {
      swal({
        title: "Error",
        text: "Nama Instansi dan Keperluan harus diisi",
        icon: "error",
        button: "OK",
      });
      return;
    }

    if (!bukuDinas || !bukuDinas.id) {
      swal({
        title: "Error",
        text: "Data buku dinas tidak valid",
        icon: "error",
        button: "OK",
      });
      return;
    }

    // Langsung buka dialog rating tanpa konfirmasi
    setSelectedRating(0);
    setRatingDialogOpen(true);
  };

  const handleRatingSubmit = async () => {
    if (selectedRating === 0) {
      swal({
        title: "Perhatian",
        text: "Silakan pilih penilaian terlebih dahulu",
        icon: "warning",
        button: "OK",
      });
      return;
    }

    setRatingDialogOpen(false);
    setSubmitting(true);

    try {
      const payload = {
        id_buku_dinas: bukuDinas.id,
        id_smart: formData.id_smart,
        nama: formData.nama,
        nama_instansi: formData.nama_instansi,
        keperluan: formData.keperluan,
        bidang_id: formData.bidang_id,
        bertemu_dengan: formData.bertemu_dengan,
        nilai: selectedRating,
      };

      await axios.post(`${API_ESA_OFFICE}buku-tamu/buku-tamu`, payload);

      swal({
        title: "Terima Kasih!",
        html: `
          <div style="text-align: center; padding: 20px;">
            <div style="font-size: 64px; margin-bottom: 20px;">🙏</div>
            <p style="font-size: 16px; line-height: 1.6; margin: 10px 0;">
              Terima kasih sudah mengisi buku tamu.
            </p>
            <p style="font-size: 16px; line-height: 1.6; margin: 10px 0;">
              Data kunjungan Anda telah tercatat dengan baik.
            </p>
            <p style="font-size: 14px; line-height: 1.6; margin: 10px 0; color: #666;">
              Terima kasih atas penilaian Anda.
            </p>
          </div>
        `,
        icon: "success",
        button: "OK",
      }).then(() => {
        // Reset form
        setFormData({
          nama: formData.nama || "", // Keep nama if exists
          nama_instansi: formData.nama_instansi || "", // Keep nama_instansi if exists
          keperluan: "",
          bidang_id: null,
          bertemu_dengan: null,
          id_smart: formData.id_smart, // Keep id_smart if exists
        });
        setSelectedRating(0);
      });
    } catch (error) {
      console.error("Error submitting buku tamu:", error);
      swal({
        title: "Error",
        text:
          error.response?.data?.message || "Gagal mengirimkan data buku tamu",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRatingClick = (rating) => {
    setSelectedRating(rating);
  };

  const handleCancelRating = async () => {
    const cancelResult = await swal({
      title: "Konfirmasi",
      text: "Apakah Anda yakin ingin membatalkan? Penilaian wajib diisi untuk melanjutkan.",
      icon: "warning",
      buttons: ["Tidak", "Ya, Batalkan"],
      dangerMode: true,
    });

    if (cancelResult) {
      setRatingDialogOpen(false);
      setSelectedRating(0);
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
            <MDTypography variant="body2" color="text" ml={2}>
              Memuat data...
            </MDTypography>
          </MDBox>
        </Container>
      </ThemeProvider>
    );
  }

  if (!bukuDinas) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
          <Card>
            <CardContent>
              <MDTypography variant="h6" color="error" textAlign="center">
                Link buku tamu tidak valid atau tidak ditemukan
              </MDTypography>
            </CardContent>
          </Card>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Card>
          {/* Banner Header */}
          <MDBox
            sx={{
              position: "relative",
              width: "100%",
              height: "200px",
              overflow: "hidden",
              borderRadius: "8px 8px 0 0",
            }}
          >
            <img
              src={bannerImage}
              alt="ESA Office Banner"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </MDBox>

          {/* Informasi - Dipindahkan ke bawah banner */}
          <MDBox p={4} pb={2} textAlign="center">
            <MDTypography variant="h4" color="text" fontWeight="bold" mb={1}>
              Buku Tamu
            </MDTypography>
            <MDTypography variant="h6" color="text" mb={3}>
              {bukuDinas.nama_dinas}
            </MDTypography>
          </MDBox>

          <MDBox p={4} pt={2}>
            <MDTypography variant="body2" color="text.secondary" mb={3}>
              Silakan isi form di bawah ini untuk mencatat kunjungan Anda.
            </MDTypography>

            <MDBox mb={3}>
              <MDInput
                label="Nama"
                name="nama"
                value={formData.nama}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
              />
            </MDBox>

            <MDBox mb={3}>
              <MDInput
                label="Nama Instansi *"
                name="nama_instansi"
                value={formData.nama_instansi}
                onChange={handleInputChange}
                fullWidth
                required
                variant="outlined"
              />
            </MDBox>

            <MDBox mb={3}>
              <Autocomplete
                freeSolo
                options={listKeperluan}
                value={formData.keperluan}
                onChange={handleKeperluanChange}
                onInputChange={(event, newInputValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    keperluan: newInputValue || "",
                  }));
                }}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label="Keperluan *"
                    variant="outlined"
                    fullWidth
                    required
                    multiline
                    rows={3}
                  />
                )}
              />
            </MDBox>

            <MDBox mb={3}>
              <Autocomplete
                options={bidangList}
                getOptionLabel={(option) => option.nama_bidang || ""}
                value={
                  bidangList.find((b) => b.bidang_id === formData.bidang_id) ||
                  null
                }
                onChange={handleBidangChange}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label="Bidang"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </MDBox>

            <MDBox mb={3}>
              <Autocomplete
                options={usersList}
                getOptionLabel={(option) => option.nama || ""}
                value={
                  usersList.find(
                    (u) => u.uid_RSS === formData.bertemu_dengan
                  ) || null
                }
                onChange={handleBertemuDenganChange}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label="Bertemu Dengan"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </MDBox>

            <MDBox display="flex" justifyContent="flex-end" mt={4}>
              <MDButton
                variant="gradient"
                color="success"
                onClick={handleSubmit}
                disabled={submitting}
                startIcon={
                  submitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Icon>send</Icon>
                  )
                }
              >
                {submitting ? "Mengirim..." : "Kirim"}
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>

        {/* Dialog Rating Bintang */}
        <Dialog
          open={ratingDialogOpen}
          onClose={() => {}} // Disable close on backdrop click
          maxWidth="md"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle>
            <MDTypography variant="h5" color="text" fontWeight="bold">
              Penilaian Layanan
            </MDTypography>
          </DialogTitle>
          <DialogContent>
            <MDBox textAlign="center" py={2}>
              {/* Tampilkan informasi pegawai jika ada yang dipilih */}
              {formData.bertemu_dengan &&
                (() => {
                  const selectedUser = usersList.find(
                    (u) => u.uid_RSS === formData.bertemu_dengan
                  );
                  if (selectedUser) {
                    const photoUrl = `https://smartdh.rotendaokab.go.id/uploads/${selectedUser.uid_RSS}.jpg`;
                    return (
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        mb={3}
                      >
                        <Avatar
                          src={photoUrl}
                          alt={selectedUser.nama || "Foto Profil"}
                          sx={{
                            width: "100px",
                            height: "100px",
                            border: "3px solid",
                            borderColor: "primary.main",
                            mb: 2,
                            boxShadow: 3,
                            fontSize: "2.5rem",
                            bgcolor: "info",
                          }}
                        >
                          {selectedUser.nama
                            ? selectedUser.nama.charAt(0).toUpperCase()
                            : "👤"}
                        </Avatar>
                        <MDTypography
                          variant="h6"
                          color="text"
                          fontWeight="medium"
                        >
                          {selectedUser.nama || "Pegawai"}
                        </MDTypography>
                        <MDTypography variant="body2" color="text.secondary">
                          Pegawai yang Anda temui
                        </MDTypography>
                      </MDBox>
                    );
                  }
                  return null;
                })()}
              <MDTypography variant="body2" color="text" mb={2}>
                Silakan berikan penilaian Anda terhadap layanan yang Anda
                terima:
              </MDTypography>
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
                sx={{
                  py: 3,
                }}
              >
                {[1, 2, 3, 4, 5].map((rating) => (
                  <MDBox
                    key={rating}
                    onClick={() => handleRatingClick(rating)}
                    sx={{
                      cursor: "pointer",
                      width: "75px",
                      height: "75px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": {
                        transform: "scale(1.1)",
                        transition: "transform 0.2s",
                      },
                      transition: "transform 0.2s",
                      "@media (max-width: 600px)": {
                        width: "50px",
                        height: "50px",
                      },
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="60"
                      height="60"
                      fill={selectedRating >= rating ? "#FFD700" : "#E0E0E0"}
                      style={{
                        transition: "fill 0.2s",
                      }}
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  </MDBox>
                ))}
              </MDBox>
              {selectedRating > 0 && (
                <MDTypography
                  variant="body2"
                  color="text.secondary"
                  mt={2}
                  sx={{ fontStyle: "italic" }}
                >
                  Anda memberikan {selectedRating} bintang
                  {selectedRating === 1
                    ? " (Sangat Tidak Puas)"
                    : selectedRating === 2
                    ? " (Tidak Puas)"
                    : selectedRating === 3
                    ? " (Cukup)"
                    : selectedRating === 4
                    ? " (Puas)"
                    : " (Sangat Puas)"}
                </MDTypography>
              )}
            </MDBox>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={handleCancelRating}
            >
              Batal
            </MDButton>
            <MDButton
              variant="gradient"
              color="success"
              onClick={handleRatingSubmit}
              disabled={selectedRating === 0}
              startIcon={<Icon>send</Icon>}
            >
              Kirim
            </MDButton>
          </DialogActions>
        </Dialog>
      </Container>
    </ThemeProvider>
  );
}

export default FormBukuTamuPublik;
